import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { DocumentTypeService } from '../../../services/documents/document-type.service';

@Component({
  selector: 'app-document-type-tree-select',
  templateUrl: './document-type-tree-select.component.html',
  styleUrls: ['./document-type-tree-select.component.css']
})
export class DocumentTypeTreeSelectComponent implements OnInit, OnChanges {
  @ViewChild('documentTypeFilterInput') documentTypeFilterInput: ElementRef<HTMLInputElement>;

  @Input() documentTypeId: number | string | undefined;
  @Input() placeholder: string = 'Filter document types';
  @Input() editable: boolean = true;
  @Input() showInactive: boolean = false;
  @Input() maxHeight: number = 320;

  @Output() document_type_id: EventEmitter<number> = new EventEmitter();
  @Output() document_type: EventEmitter<any> = new EventEmitter();
  @Output() clearDocumentType: EventEmitter<any> = new EventEmitter();

  documentTypeTree: Array<any> = [];
  visibleDocumentTypeTree: Array<any> = [];
  documentTypesById: any = {};
  expandedNodes: any = {};
  filterText: string = '';
  selectedDocumentType: any = null;
  loading: boolean = true;

  constructor(private documentTypeService: DocumentTypeService) {}

  ngOnInit() {
    this.loadDocumentTypes();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.documentTypeId && !changes.documentTypeId.firstChange) {
      this.syncSelectedDocumentType();
    }
  }

  loadDocumentTypes() {
    this.loading = true;
    this.documentTypeService.tree({ show_inactive: this.showInactive }).then((response: any) => {
      this.documentTypeTree = response.document_type_tree || [];
      this.documentTypesById = {};
      this.indexDocumentTypes(this.documentTypeTree);
      this.syncSelectedDocumentType();
      this.refreshVisibleTree();
      this.loading = false;
    });
  }

  indexDocumentTypes(nodes: Array<any>) {
    for (let node of this.documentTypeArray(nodes)) {
      this.documentTypesById[String(node.id)] = node;
      this.indexDocumentTypes(this.documentTypeArray(node.children));
    }
  }

  syncSelectedDocumentType() {
    if (!this.documentTypeId || !this.documentTypesById) {
      this.selectedDocumentType = null;
      return;
    }

    this.selectedDocumentType = this.documentTypesById[String(this.documentTypeId)] || this.selectedDocumentType;
  }

  documentTypeArray(nodes: any): Array<any> {
    if (!nodes) return [];
    return Array.isArray(nodes) ? nodes : Object.values(nodes);
  }

  refreshVisibleTree() {
    this.visibleDocumentTypeTree = this.documentTypeNodesForDisplay(this.documentTypeTree);
  }

  applyFilter(filterText: string) {
    this.filterText = filterText || '';
    this.refreshVisibleTree();
  }

  clearFilter() {
    this.filterText = '';
    this.refreshVisibleTree();
  }

  documentTypeNodesForDisplay(nodes: any = null): Array<any> {
    const filterText = (this.filterText || '').toLowerCase().trim();
    const sourceNodes = this.documentTypeArray(nodes === null ? this.documentTypeTree : nodes);
    if (!filterText) return sourceNodes;

    let visibleNodes: Array<any> = [];
    for (let node of sourceNodes) {
      const nodeChildren = this.documentTypeArray(node.children);
      const visibleChildren = this.documentTypeNodesForDisplay(nodeChildren);
      const nodeMatches =
        (node.name || '').toLowerCase().includes(filterText) ||
        (node.prefix || '').toLowerCase().includes(filterText) ||
        (node.label || '').toLowerCase().includes(filterText) ||
        (node.full_name || '').toLowerCase().includes(filterText);

      if (nodeMatches || visibleChildren.length > 0) {
        visibleNodes.push({ ...node, children: nodeMatches ? nodeChildren : visibleChildren });
      }
    }
    return visibleNodes;
  }

  nodeExpanded(node: any): boolean {
    return !!this.expandedNodes[node.key] || !!(this.filterText || '').trim();
  }

  toggleNode(node: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.expandedNodes[node.key] = !this.expandedNodes[node.key];
  }

  selectDocumentType(node: any) {
    if (!this.editable || !node || !node.id) return;

    this.documentTypeId = node.id;
    this.selectedDocumentType = this.documentTypesById[String(node.id)] || node;
    this.document_type.emit(this.selectedDocumentType);
    this.document_type_id.emit(Number(node.id));
  }

  clearSelectedDocumentType() {
    if (!this.editable) return;

    this.documentTypeId = undefined;
    this.selectedDocumentType = null;
    this.clearDocumentType.emit('clearDocumentType');
    window.setTimeout(() => {
      if (this.documentTypeFilterInput) this.documentTypeFilterInput.nativeElement.focus();
    });
  }

  selected(node: any): boolean {
    return !!node && !!this.documentTypeId && String(node.id) === String(this.documentTypeId);
  }

  displayPrefix(node: any): string {
    return String((node && node.prefix) || '').trim();
  }

  selectedDocumentTypeLabel(): string {
    if (!this.selectedDocumentType) return 'No document type selected';
    return this.selectedDocumentType.label || `${this.selectedDocumentType.prefix}: ${this.selectedDocumentType.name}`;
  }
}
